import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Headphones,
  Loader2,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import SpacingBox from "./Spacing";
import AudioPlayer from "./AudioPlayer";
import WriteBox from "./WordBox";
import useFetchData from "../fetchpostData";
import ScoreBoxForWritting from "./ResponseBoxForWrittingSection";
import Notification from "./Notification";
import Timers from "../PageComponent/Timers";
import Timer from "../MockTest/Timer";

const ListeningShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

const getAnalysisTone = (type) => {
  if (type === "correct") return "bg-green-50 text-green-700 ring-green-100";
  if (type === "missing") return "bg-amber-50 text-amber-700 ring-amber-100";
  if (type === "extra") return "bg-slate-100 text-slate-600 ring-slate-200";
  return "bg-red-50 text-red-700 ring-red-100";
};

const formatPercent = (value) => `${Math.round((value || 0) * 100)}%`;

const WordAnalysis = ({ analysis = [] }) => (
  <div className="flex flex-wrap gap-2">
    {analysis.map((item, index) => (
      <span
        key={`${item.type}-${item.expected || item.actual}-${index}`}
        className={`inline-flex min-h-9 items-center rounded-md px-3 py-1.5 text-sm font-bold ring-1 ${getAnalysisTone(
          item.type
        )}`}
        title={
          item.type === "incorrect"
            ? `Expected: ${item.expected}`
            : item.type === "missing"
              ? "Missing word"
              : item.type === "extra"
                ? "Extra word"
                : "Correct word"
        }
      >
        {item.type === "missing"
          ? item.expected
          : item.type === "incorrect"
            ? `${item.actual} (${item.expected})`
            : item.actual}
      </span>
    ))}
  </div>
);

const DictationScoreCard = ({ savedPractice }) => {
  const score = savedPractice?.writeFromDictationScore;
  const savedResponse = savedPractice?.savedResponse;
  const totalScore = savedResponse?.totalScore ?? score?.overall_score ?? "N/A";
  const maxScore =
    savedPractice?.maxScores?.overall ||
    savedResponse?.maxScore ||
    score?.max_score ||
    0;

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-0 lg:grid-cols-[minmax(260px,0.9fr),1.1fr]">
        <div className="bg-violet-600 p-6 text-white">
          <p className="text-sm font-semibold uppercase text-violet-100">
            Overall score
          </p>
          <h3 className="mt-1 text-2xl font-extrabold">
            Write From Dictation
          </h3>
          <div className="mt-8 flex items-end gap-2">
            <span className="text-6xl font-extrabold leading-none">
              {totalScore}
            </span>
            <span className="pb-2 text-sm font-bold text-violet-100">
              / {maxScore || "N/A"}
            </span>
          </div>
          <div className="mt-6 rounded-md bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase text-violet-100">
              Remarks
            </p>
            <p className="mt-1 text-xl font-extrabold">
              {savedResponse?.remarks || "Awaiting feedback"}
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <p className="text-sm font-semibold uppercase text-purple-700">
            AI analysis
          </p>
          <h4 className="mt-1 text-xl font-extrabold text-gray-900">
            Word accuracy
          </h4>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">
                Correct words
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                {score?.correct_word_count ?? totalScore}/{maxScore || "N/A"}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">
                Accuracy
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                {formatPercent(score?.content_accuracy)}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">
                Missing
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                {score?.missed_words?.length || 0}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase text-gray-500">
                Extra
              </p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">
                {score?.extra_words?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WriteFromDictationPage() {
  const { title } = useParams();

  const section = "writefromdictation";
  const practiceItemId = title;

  const post = useFetchData(section, title);
  const [originalWords, setOriginalWords] = useState("");
  const [Title, setTitle] = useState("");
  const [audioLink, setaudioLink] = useState("");
  const [text, setText] = useState("");
  const [originalAnswer, setOriginalAnswer] = useState("");
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTextChange = (newText) => {
    setText(newText);
  };

  useEffect(() => {
    console.log("Post Data :", post);

    if (post && post.media_link) {
      setOriginalWords(post.question);
      setTitle(post.q_title);
      setOriginalAnswer(post.audio_script);

      const fileName = encodeURIComponent(post.media_link.split("/").pop());

      fetch(`/ptedata/ptemedia/${fileName}`, { credentials: "include" })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setaudioLink(data.url);
          } else {
            console.error("Failed to fetch image URL");
          }
        })
        .catch((error) => console.error("Error fetching signed URL:", error));
    }
  }, [post]);

  console.log("Audio Link:", audioLink);
  console.log("Original Word:", originalWords);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-writefromdictation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    const answer = text;
    const practiceType = section;

    e.preventDefault();
    setLoading(true);

    const practiceData = {
      practiceType,
      practiceItemId,
      response: {
        questionId: practiceItemId,
        answer,
        isAttempted: true,
      },
    };
    console.log("practiceData:", practiceData);
    try {
      const result = await savePractice(practiceData);
      console.log("Practice data saved:", result);
      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
        setSubmitted(true);
      }, 2000);
    } catch (error) {
      console.error("Error saving practice data:", error);
      setMessage("Failed to save practice data.");
      setNotificationType("error");
      setShowNotification(true);
      setLoading(false);
      setSubmitted(false);
    }
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  console.log("savedPractice", savedPractice);

  const handleIsAttempted = (isAttemptedValue) => {
    setIsAttempted(isAttemptedValue);
  };

  const attemptedStatus = isAttempted ? "Attempted" : "Not Attempted";

  return (
    <ListeningShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Listening practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {Title || "Write from dictation"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Listen once and type the sentence exactly as you hear it.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {attemptedStatus}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Type the sentence
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  You will hear a sentence once. Type it in the box below
                  exactly as you hear it, writing as much of the sentence as you
                  can.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Headphones className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Audio
                </p>
                <h2 className="text-lg font-extrabold text-gray-900">
                  Sentence playback
                </h2>
              </div>
            </div>
            {audioLink && <AudioPlayer audioSrc={audioLink} />}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Writing window
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Type what you heard
                </h2>
              </div>
              <span className="inline-flex min-h-10 items-center rounded-md bg-purple-50 px-3 py-2 text-sm font-bold text-purple-800 ring-1 ring-purple-100">
                <Timer
                  initialMinutes={10}
                  initialSeconds={0}
                  Text="Time remaining"
                />
              </span>
            </div>

            <WriteBox
              onTextChange={handleTextChange}
              minHeight="min-h-[12rem]"
            />
          </section>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit when your typed sentence is as complete and accurate as
                  possible.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Submit
                </>
              )}
            </button>
          </section>

          {submitted ? (
            <section className="space-y-5">
              <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-md bg-green-50 text-green-700">
                    <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase text-purple-700">
                      Result
                    </p>
                    <h2 className="text-xl font-extrabold text-gray-900">
                      Latest writing score
                    </h2>
                  </div>
                </div>

                <DictationScoreCard savedPractice={savedPractice} />
              </div>

              {savedPractice?.writeFromDictationScore ? (
                <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Correct and incorrect words
                  </p>
                  <h3 className="mt-1 text-xl font-extrabold text-gray-900">
                    Word-by-word comparison
                  </h3>
                  <div className="mt-4 rounded-md bg-slate-50 p-4 ring-1 ring-slate-100">
                    <WordAnalysis
                      analysis={
                        savedPractice.writeFromDictationScore.word_analysis
                      }
                    />
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-md bg-green-50 p-4 ring-1 ring-green-100">
                      <p className="text-xs font-semibold uppercase text-green-700">
                        Correct answer
                      </p>
                      <p className="mt-1 text-sm font-bold leading-6 text-green-900">
                        {savedPractice.writeFromDictationScore.expected_tokens?.join(
                          " "
                        )}
                      </p>
                    </div>
                    <div className="rounded-md bg-blue-50 p-4 ring-1 ring-blue-100">
                      <p className="text-xs font-semibold uppercase text-blue-700">
                        AI feedback
                      </p>
                      <p className="mt-1 text-sm font-bold leading-6 text-blue-900">
                        {
                          savedPractice.writeFromDictationScore.feedback
                            ?.overall
                        }
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Capture the subject, verb, and ending first. Then add smaller
                  words if you remember them.
                </p>
              </div>
            </div>
          </section>

          <ScoreBoxForWritting
            originalWords={originalAnswer}
            practiceItemId={practiceItemId}
            onIsAttempted={handleIsAttempted}
          />
        </aside>
      </div>

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={handleCloseNotification}
        />
      )}

      <SpacingBox />
    </ListeningShell>
  );
}
